"use client"

import { useCallback, useEffect, useState } from "react"

export type InstallPlatform = "ios" | "android" | "desktop" | "unknown"

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
  prompt(): Promise<void>
}

const DISMISSED_KEY = "mobile_cta_dismissed"
const SEEN_KEY = "mobile_cta_seen"

function detectPlatform(): InstallPlatform {
  if (typeof navigator === "undefined") return "unknown"
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/i.test(ua)) return "ios"
  if (/Android/i.test(ua)) return "android"
  return "desktop"
}

function readFlag(key: string): boolean {
  if (typeof window === "undefined") return false
  try {
    return window.localStorage.getItem(key) === "1"
  } catch {
    return false
  }
}

function writeFlag(key: string, value: boolean) {
  if (typeof window === "undefined") return
  try {
    if (value) window.localStorage.setItem(key, "1")
    else window.localStorage.removeItem(key)
  } catch {
    /* swallow */
  }
}

export interface UseMobileInstallResult {
  platform: InstallPlatform
  isInstalled: boolean
  isInstallable: boolean
  isDismissed: boolean
  isSeen: boolean
  install: () => Promise<"accepted" | "dismissed" | "unavailable">
  dismiss: () => void
  resetDismiss: () => void
  markSeen: () => void
}

export function useMobileInstall(): UseMobileInstallResult {
  const [platform, setPlatform] = useState<InstallPlatform>("unknown")
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const [isSeen, setIsSeen] = useState(false)

  useEffect(() => {
    setPlatform(detectPlatform())
    setIsDismissed(readFlag(DISMISSED_KEY))
    setIsSeen(readFlag(SEEN_KEY))

    const checkStandalone = () => {
      if (typeof window === "undefined") return
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true
      setIsInstalled(standalone)
    }
    checkStandalone()

    const onBeforeInstall = (event: Event) => {
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }
    const mql = window.matchMedia("(display-mode: standalone)")
    const onModeChange = () => checkStandalone()

    window.addEventListener("beforeinstallprompt", onBeforeInstall)
    window.addEventListener("appinstalled", onInstalled)
    mql.addEventListener("change", onModeChange)

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall)
      window.removeEventListener("appinstalled", onInstalled)
      mql.removeEventListener("change", onModeChange)
    }
  }, [])

  const install = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    if (!deferredPrompt) return "unavailable"
    try {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      setDeferredPrompt(null)
      return choice.outcome
    } catch {
      return "unavailable"
    }
  }, [deferredPrompt])

  const dismiss = useCallback(() => {
    writeFlag(DISMISSED_KEY, true)
    setIsDismissed(true)
  }, [])

  const resetDismiss = useCallback(() => {
    writeFlag(DISMISSED_KEY, false)
    setIsDismissed(false)
  }, [])

  const markSeen = useCallback(() => {
    writeFlag(SEEN_KEY, true)
    setIsSeen(true)
  }, [])

  return {
    platform,
    isInstalled,
    isInstallable: Boolean(deferredPrompt) || platform === "ios",
    isDismissed,
    isSeen,
    install,
    dismiss,
    resetDismiss,
    markSeen,
  }
}
